import type {
  AssistantActivityTurnBlock,
  AssistantActivityTurnItem,
  AssistantActivityTurnTimelineItem,
} from "../../types";
import type { AssistantOperationApprovalGate } from "../assistant-operation-approval-card";

export type RenderableActivityBlock = {
  block: AssistantActivityTurnBlock;
  headerItem: AssistantActivityTurnItem | null;
  workItems: AssistantActivityTurnItem[];
};

export function isActiveWorkItem(item: AssistantActivityTurnItem) {
  if (item.kind !== "tool_activity" && item.kind !== "runtime_status") {
    return false;
  }
  return item.status === "pending" || item.status === "in_progress";
}

export function getActivityTurnItemFlags(items: AssistantActivityTurnItem[]) {
  let hasActiveWorkItem = false;
  let hasWaitingApprovalItem = false;
  let hasWaitingUserInputItem = false;
  let hasWaitingItem = false;
  let hasExpandableItem = false;

  for (const item of items) {
    if (isActiveWorkItem(item)) {
      hasActiveWorkItem = true;
    }
    if (item.kind === "approval" && item.status === "waiting") {
      hasWaitingApprovalItem = true;
    }
    if (item.kind === "user_input" && item.status === "waiting") {
      hasWaitingUserInputItem = true;
    }
    if (item.kind === "waiting" && item.status === "waiting") {
      hasWaitingItem = true;
    }
    if (
      item.kind === "preamble" ||
      item.kind === "assistant_progress" ||
      item.kind === "approval" ||
      (item.debugEntries?.length ?? item.entries?.length ?? 0) > 1
    ) {
      hasExpandableItem = true;
    }
  }

  return {
    hasActiveWorkItem,
    hasWaitingApprovalItem,
    hasWaitingUserInputItem,
    hasWaitingItem,
    hasExpandableItem,
  };
}

export function toApprovalGate(
  turn: AssistantActivityTurnTimelineItem,
  item: AssistantActivityTurnItem
): AssistantOperationApprovalGate | null {
  if (item.kind !== "approval" || !item.approval) {
    return null;
  }
  return {
    runId: turn.runId,
    // The turn's own thread, carried so the approve/deny mutations can be cross-checked against
    // it — a gate answered in one conversation must not reach a run in another (LAT-990).
    threadId: turn.threadId,
    title: item.approval.title,
    description: item.approval.description,
    badgeLabel: item.approval.badgeLabel,
    badgeTone: item.approval.badgeTone,
    primaryActionLabel: item.approval.primaryActionLabel,
    secondaryActionLabel: item.approval.secondaryActionLabel,
    assistantToolCallId: item.approval.assistantToolCallId ?? null,
    assistantToolCallIds: item.approval.assistantToolCallIds,
    toolCallSummaries: item.approval.toolCallSummaries,
    toolCallSummaryParts: item.approval.toolCallSummaryParts,
    titleParts: item.approval.titleParts,
    descriptionParts: item.approval.descriptionParts,
  };
}

export function getRenderableBlocks(
  turn: Pick<AssistantActivityTurnTimelineItem, "blocks" | "items">
): RenderableActivityBlock[] {
  if (!turn.blocks?.length) {
    return [];
  }

  const itemsById = new Map<string, AssistantActivityTurnItem>();
  for (const item of turn.items) {
    itemsById.set(item.id, item);
  }
  return turn.blocks.map((block) => {
    const workItems: AssistantActivityTurnItem[] = [];
    let headerItem = block.headerItemId ? (itemsById.get(block.headerItemId) ?? null) : null;

    for (const itemId of block.itemIds) {
      const item = itemsById.get(itemId);
      if (!item) {
        continue;
      }
      if (item.id === block.headerItemId) {
        headerItem = item;
        continue;
      }
      workItems.push(item);
    }

    return {
      block,
      headerItem,
      workItems,
    };
  });
}

export function getSafeEntryLabels(item: AssistantActivityTurnItem) {
  const labels: string[] = [];
  for (const entry of item.entries ?? []) {
    if (typeof entry.displayLabel === "string" && entry.displayLabel.trim().length > 0) {
      labels.push(entry.displayLabel.trim());
      continue;
    }

    const message = typeof entry.message === "string" ? entry.message.trim() : "";
    if (!message || message.startsWith("{") || message.startsWith("[")) {
      continue;
    }
    labels.push(message);
  }
  return labels;
}
